import type { InfractionStatus } from "../types/infraction.types";
import {
	API_STATUS_TO_INFRACTION_STATUS,
	INFRACTION_DISPLAY_STATUS,
} from "./infraction-api-enums.constant";
import { INFRACTION_STATUS } from "./infraction-status.constant";

export type InfractionStatusBadgePalette = {
	root: string;
	dot: string;
	label: string;
};

const warningPalette: InfractionStatusBadgePalette = {
	root: "border-utility-warning-200 bg-utility-warning-50",
	dot: "bg-utility-warning-500",
	label: "text-utility-warning-700",
};

const errorPalette: InfractionStatusBadgePalette = {
	root: "border-utility-error-200 bg-utility-error-50",
	dot: "bg-utility-error-500",
	label: "text-utility-error-700",
};

const displayStatusBadgeStyles: Record<number, InfractionStatusBadgePalette> = {
	[INFRACTION_DISPLAY_STATUS.NOTIFICATION_RECEIVED]: {
		root: "border-border-primary bg-bg-secondary",
		dot: "bg-utility-gray-500",
		label: "text-fg-primary",
	},
	[INFRACTION_DISPLAY_STATUS.AWAITING_DRIVER_INDICATION]: warningPalette,
	[INFRACTION_DISPLAY_STATUS.DRIVER_INDICATION_EXPIRED]: errorPalette,
	[INFRACTION_DISPLAY_STATUS.PENDING_PAYMENT]: warningPalette,
	[INFRACTION_DISPLAY_STATUS.PAYMENT_EXPIRED]: errorPalette,
	[INFRACTION_DISPLAY_STATUS.DRIVER_ASSIGNED]: {
		root: "border-utility-success-200 bg-utility-success-50",
		dot: "bg-utility-success-500",
		label: "text-utility-success-700",
	},
	[INFRACTION_DISPLAY_STATUS.PAID]: {
		root: "border-utility-indigo-200 bg-utility-indigo-50",
		dot: "bg-utility-indigo-500",
		label: "text-utility-indigo-700",
	},
	[INFRACTION_DISPLAY_STATUS.CANCELLED]: {
		root: "border-utility-orange-200 bg-utility-orange-50",
		dot: "bg-utility-orange-500",
		label: "text-utility-orange-700",
	},
};

const defaultBadgeStyles =
	displayStatusBadgeStyles[INFRACTION_DISPLAY_STATUS.NOTIFICATION_RECEIVED];

const domainStatusBadgeStyles: Record<
	InfractionStatus,
	InfractionStatusBadgePalette
> = {
	[INFRACTION_STATUS.NOTIFICATION_RECEIVED]:
		displayStatusBadgeStyles[INFRACTION_DISPLAY_STATUS.NOTIFICATION_RECEIVED],
	[INFRACTION_STATUS.AWAITING_DRIVER_INDICATION]:
		displayStatusBadgeStyles[
			INFRACTION_DISPLAY_STATUS.AWAITING_DRIVER_INDICATION
		],
	[INFRACTION_STATUS.DRIVER_INDICATED]:
		displayStatusBadgeStyles[INFRACTION_DISPLAY_STATUS.DRIVER_ASSIGNED],
	[INFRACTION_STATUS.IN_DEFENSE]: {
		root: "border-utility-blue-200 bg-utility-blue-50",
		dot: "bg-utility-blue-500",
		label: "text-utility-blue-700",
	},
	[INFRACTION_STATUS.PAYMENT_PENDING]:
		displayStatusBadgeStyles[INFRACTION_DISPLAY_STATUS.PENDING_PAYMENT],
	[INFRACTION_STATUS.PAYMENT_EXPIRED]:
		displayStatusBadgeStyles[INFRACTION_DISPLAY_STATUS.PAYMENT_EXPIRED],
	[INFRACTION_STATUS.PAID]:
		displayStatusBadgeStyles[INFRACTION_DISPLAY_STATUS.PAID],
	[INFRACTION_STATUS.EXPIRED]:
		displayStatusBadgeStyles[
			INFRACTION_DISPLAY_STATUS.DRIVER_INDICATION_EXPIRED
		],
	[INFRACTION_STATUS.CANCELLED]:
		displayStatusBadgeStyles[INFRACTION_DISPLAY_STATUS.CANCELLED],
	[INFRACTION_STATUS.WARNING]: warningPalette,
};

/** Cores do badge por `displayStatus` da API (Figma 8455-10746). */
export function resolveInfractionDisplayStatusBadgeStyles(
	displayStatus: number
): InfractionStatusBadgePalette {
	return displayStatusBadgeStyles[displayStatus] ?? defaultBadgeStyles;
}

export function resolveInfractionStatusBadgeStyles(
	status: InfractionStatus
): InfractionStatusBadgePalette {
	return domainStatusBadgeStyles[status] ?? defaultBadgeStyles;
}

export function resolveInfractionStatusFromApiCode(
	statusCode: number
): InfractionStatus {
	return (
		API_STATUS_TO_INFRACTION_STATUS[statusCode] ??
		INFRACTION_STATUS.NOTIFICATION_RECEIVED
	);
}
